<template>
  <hr-form-dialog
    ref="dialog"
    size="md"
    :title="$t('sessions.noteTemplate.changeRequest.title')"
    @hide="onDialogHide"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-time-field
        :model-value="convertTimestamp(startTime)"
        :label="$t('sessions.noteTemplate.changeRequest.inputStartTime')"
        :allow-empty="!$q.platform.is.safari"
        preserve-date
        hide-bottom-space
        class="col-12 col-sm-6"
        @update:model-value="onInputTime($event, 'startTime')"
      />
      <hr-time-field
        :model-value="convertTimestamp(endTime)"
        :label="$t('sessions.noteTemplate.changeRequest.inputEndTime')"
        :allow-empty="!$q.platform.is.safari"
        preserve-date
        hide-bottom-space
        class="col-12 col-sm-6"
        @update:model-value="onInputTime($event, 'endTime')"
      />
      <hr-text-field
        :model-value="requestReason"
        :label="$t('sessions.noteTemplate.changeRequest.inputReason')"
        :placeholder="$t('sessions.noteTemplate.changeRequest.inputReasonPlaceholder')"
        :rules="rules.inputValidationRules"
        hide-bottom-space
        class="col-12"
        @update:model-value="onInputValue"
      />
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <universal-tooltip
        :show-tooltip="isSubmitDisabled"
        :text="$t('sessions.noteTemplate.changeRequest.tooltipRequireReason')"
      >
        <hr-confirm-btn
          type="submit"
          :label="$t('sessions.noteTemplate.changeRequest.btnSubmitRequest')"
          :disable="isSubmitDisabled"
        />
      </universal-tooltip>
    </template>
  </hr-form-dialog>
</template>
<script>

import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import {
  isDate,
  isEmpty,
} from 'lodash';
import { mapState } from 'pinia';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrTextField,
  HrTimeField,
} from 'src/design-system';
import { auth } from 'src/store/auth';

export default {
  name: 'SessionActivityChangeRequest',

  components: {
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrTextField,
    HrTimeField,
    UniversalTooltip,
  },

  props: {
    sessionNoteFields: {
      type: Array,
      default: () => ([]),
    },
    sessionActivity: {
      type: Object,
      required: true,
    },
    newRequestAfterRejection: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      changeRequest: {},
      startTime: null,
      endTime: null,
      requestReason: '',

      rules: {
        inputValidationRules: [
          (val) => {
            if (isEmpty(val.trim())) {
              return this.$t('sessions.noteTemplate.validateSessionNoteField');
            }
            return true;
          },
        ],
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),

    isSubmitDisabled() {
      return !this.requestReason.trim();
    },

  },

  created() {
    this.sessionNoteFields.forEach((field) => {
      switch (field.id) {
        case 'sessionStartTime': {
          this.startTime = field.value;
          break;
        }
        case 'sessionFinishTime': {
          this.endTime = field.value;
          break;
        }
        default:
          break;
      }
    });
  },

  methods: {
    show() {
      this.$refs.dialog.show();
    }, // show()

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    async onDialogHide() {
      this.$emit('hide');
    }, // onDialogHide()

    async onSubmit() {
      this.changeRequest = {
        status: this.newRequestAfterRejection ? 'pending' : 'draft',
        reason: this.requestReason,
        user: this.userDetails.userId,
        fields: [
          { startTimestamp: this.startTime },
          { endTimestamp: this.endTime },
        ],
      };

      this.$emit('ok', this.changeRequest);
      this.hide();
    }, // onSubmit()

    onInputValue(value) {
      this.requestReason = value;
    }, // onInputValue()

    onInputTime(selectedDate, field) {
      if (selectedDate instanceof Timestamp) {
        switch (field) {
          case 'startTime': {
            const adjustDateFunc = this.sessionNoteFields.find(({ id }) => id === 'sessionFinishTime')?.adjust;

            if (this.endTime && typeof adjustDateFunc === 'function' && selectedDate instanceof Timestamp) {
              const endTimestamp = Timestamp.fromDate(this.endTime);
              const adjustedDate = adjustDateFunc(endTimestamp, selectedDate);
              if (adjustedDate) {
                this.endTime = adjustedDate.toDate();
              }
            }

            this.startTime = selectedDate.toDate();
            break;
          }
          case 'endTime': {
            const adjustFunc = this.sessionNoteFields.find(({ id }) => id === 'sessionFinishTime')?.adjust;

            const startTimestamp = Timestamp.fromDate(this.startTime) || this.sessionActivity.startTime;
            if (typeof adjustFunc === 'function' && startTimestamp instanceof Timestamp) {
              const adjustedDate = adjustFunc(selectedDate, startTimestamp);
              this.endTime = adjustedDate.toDate();
              break;
            }

            this.endTime = selectedDate.toDate();
            break;
          }
          default:
            break;
        }
      }
    }, // onInputTime()

    convertTimestamp(d) {
      return isDate(d) ? Timestamp.fromDate(d) : null;
    }, // convertTimestamp()
  },
};
</script>
